import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { returnsAPI, getErrorMessage } from "../../services/api";
import AdminLayout from "../../components/admin/AdminLayout";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import toast from "react-hot-toast";
import { format } from "date-fns";

const STATUS_COLORS: Record<string, string> = {
  requested: "bg-yellow-100 text-yellow-800",
  approved: "bg-blue-100 text-blue-800",
  rejected: "bg-red-100 text-red-800",
  picked_up: "bg-purple-100 text-purple-800",
  refunded: "bg-green-100 text-green-800",
};

export default function AdminReturns() {
  const [returns, setReturns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);
  const [form, setForm] = useState({ status: "", refund_amount: "", admin_notes: "" });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    returnsAPI.adminList().then((res) => setReturns(res.data)).finally(() => setLoading(false));
  }, []);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const data: any = { status: form.status };
      if (form.refund_amount) data.refund_amount = parseFloat(form.refund_amount);
      if (form.admin_notes) data.admin_notes = form.admin_notes;
      await returnsAPI.adminUpdate(selected.id, data);
      toast.success("Return request updated!");
      setSelected(null);
      const res = await returnsAPI.adminList();
      setReturns(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <AdminLayout>
      <Helmet><title>Returns – Admin | Kushi Fashions</title></Helmet>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Return Requests</h1>
      </div>

      {/* Update modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-5">Process Return Request</h2>
            <p className="text-sm text-gray-600 mb-4">Reason: <strong>{selected.reason}</strong></p>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} className="input-field" required>
                  <option value="">Select status</option>
                  {["approved", "rejected", "picked_up", "refunded"].map((s) => (
                    <option key={s} value={s}>{s.replace("_", " ")}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Refund Amount (₹)</label>
                <input type="number" value={form.refund_amount} onChange={(e) => setForm(f => ({ ...f, refund_amount: e.target.value }))} className="input-field" placeholder="Leave empty if not refunding" step="0.01" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes</label>
                <textarea value={form.admin_notes} onChange={(e) => setForm(f => ({ ...f, admin_notes: e.target.value }))} rows={3} className="input-field resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={updating} className="btn-primary px-6 py-2.5">{updating ? "Updating..." : "Update"}</button>
                <button type="button" onClick={() => setSelected(null)} className="btn-secondary px-6 py-2.5">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingSpinner className="py-20" size="lg" />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-gray-500">Order ID</th>
                  <th className="text-left px-3 py-3 text-gray-500">Reason</th>
                  <th className="text-left px-3 py-3 text-gray-500">Status</th>
                  <th className="text-left px-3 py-3 text-gray-500">Date</th>
                  <th className="text-right px-5 py-3 text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {returns.map((ret) => (
                  <tr key={ret.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-5 py-3 font-medium text-gray-800 text-xs">{String(ret.order_id).substring(0, 8)}...</td>
                    <td className="px-3 py-3 text-gray-600">{ret.reason}</td>
                    <td className="px-3 py-3">
                      <span className={`badge ${STATUS_COLORS[ret.status] || "bg-gray-100"}`}>{ret.status.replace("_", " ")}</span>
                    </td>
                    <td className="px-3 py-3 text-gray-400 text-xs">{format(new Date(ret.created_at), "dd MMM yyyy")}</td>
                    <td className="px-5 py-3 text-right">
                      {ret.status !== "refunded" && (
                        <button
                          onClick={() => { setSelected(ret); setForm({ status: "", refund_amount: "", admin_notes: ret.admin_notes || "" }); }}
                          className="text-primary-600 hover:text-primary-700 font-medium text-xs"
                        >
                          Process
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {returns.length === 0 && (
              <div className="text-center py-12 text-gray-400">No return requests</div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
